import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Lightbulb,
  Calendar,
  DollarSign,
  ClipboardList,
  FileText,
} from 'lucide-react';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { getPatientContext } from '@/services/ai-context.service';

interface Props {
  patientId: number;
  /**
   * This endpoint generates its summary with an LLM call, so it stays off
   * until the user asks the assistant for something.
   */
  enabled?: boolean;
}

export function PatientContextPanel({ patientId, enabled = false }: Props) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ai-context', 'patient', patientId],
    queryFn: () => getPatientContext(patientId),
    staleTime: 30_000,
    enabled,
  });

  if (!enabled) return null;

  if (isLoading) return <SkeletonLoader />;

  if (isError || !data) {
    return (
      <div className="p-4 text-sm text-red-600">
        Failed to load patient context. Please try again.
      </div>
    );
  }

  const visits = data.visitSummary ?? {};
  const spending = data.spendingSummary ?? {};
  const plans = data.activePlans ?? [];
  const notes = data.recentNotes ?? [];
  const flags = data.riskFlags ?? [];
  const suggestions = data.suggestions ?? [];

  return (
    <div className="divide-y divide-slate-100">
      {/* Risk Flags */}
      {flags.length > 0 && (
        <Section icon={AlertTriangle} title="Risk Flags" iconColor="text-red-500">
          <div className="flex flex-wrap gap-1.5">
            {flags.map((flag, i) => (
              <span
                key={i}
                className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-200"
              >
                {flag}
              </span>
            ))}
          </div>
        </Section>
      )}

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <Section icon={Lightbulb} title="Suggestions" iconColor="text-amber-500">
          <ul className="space-y-2">
            {suggestions.map((suggestion, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <Lightbulb className="h-4 w-4 mt-0.5 shrink-0 text-amber-400" />
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Visit History */}
      <Section icon={Calendar} title="Visit History" iconColor="text-blue-500">
        <div className="grid grid-cols-2 gap-3">
          <StatItem label="Total Visits" value={String(visits.totalVisits ?? 0)} />
          <StatItem
            label="Last Visit"
            value={visits.lastVisitDate ? formatDate(visits.lastVisitDate) : 'N/A'}
          />
          <StatItem
            label="No-Shows"
            value={String(visits.noShowCount ?? 0)}
            className={(visits.noShowCount ?? 0) > 2 ? 'text-red-600' : undefined}
          />
          <StatItem label="Cancellations" value={String(visits.cancellationCount ?? 0)} />
        </div>
      </Section>

      {/* Spending */}
      <Section icon={DollarSign} title="Spending" iconColor="text-emerald-500">
        <div className="grid grid-cols-2 gap-3">
          <StatItem label="Total Spent" value={formatCurrency(spending.totalSpent ?? 0)} />
          <StatItem label="Avg / Visit" value={formatCurrency(spending.averagePerVisit ?? 0)} />
          <StatItem
            label="Outstanding"
            value={formatCurrency(spending.outstandingBalance ?? 0)}
            className={(spending.outstandingBalance ?? 0) > 0 ? 'text-red-600 font-medium' : undefined}
          />
        </div>
      </Section>

      {/* Active Plans */}
      {plans.length > 0 && (
        <Section icon={ClipboardList} title="Active Plans" iconColor="text-violet-500">
          <ul className="space-y-2">
            {plans.map((plan) => (
              <li
                key={plan.planId}
                className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-800">
                    Plan #{plan.planId}
                  </span>
                  {plan.stage && (
                    <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                      {plan.stage}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Recent Notes */}
      {notes.length > 0 && (
        <Section icon={FileText} title="Recent Notes" iconColor="text-slate-500">
          <ul className="space-y-2">
            {notes.map((note, i) => (
              <li key={i} className="text-sm">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="font-medium text-slate-700">{note.author}</span>
                  <span className="text-xs text-slate-400">{formatDate(note.date)}</span>
                </div>
                <p className="text-slate-600 line-clamp-2">{note.message}</p>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

// ─── Helper Components ──────────────────────────────────────────────────────

function Section({
  icon: Icon,
  title,
  iconColor,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  iconColor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className={cn('h-4 w-4', iconColor)} />
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function StatItem({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div>
      <dt className="text-xs text-slate-400">{label}</dt>
      <dd className={cn('text-sm font-semibold text-slate-800', className)}>{value}</dd>
    </div>
  );
}

function SkeletonLoader() {
  return (
    <div className="p-4 space-y-4 animate-pulse">
      <div className="rounded-lg bg-gradient-to-br from-indigo-200 to-purple-200 h-28" />
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <div className="h-3 w-24 bg-slate-200 rounded" />
          <div className="h-4 w-full bg-slate-100 rounded" />
          <div className="h-4 w-3/4 bg-slate-100 rounded" />
        </div>
      ))}
    </div>
  );
}
